import { useEffect, useMemo, useRef, useState } from "react";
import AxiosInstance from "../../utils/axios";
import {
  fetchYieldAnalysisReadiness,
  getYieldAnalysisYear,
  NOT_READY,
  type YieldAnalysisReadiness,
} from "../../utils/yieldAnalysis";
import { pickProtocolRows } from "./trialPreviewMapper";

export interface PreviewRequestHeaders {
  "org-id": string;
  "workspace-id": string;
}

/**
 * Rebuilds the request headers from their primitive values.
 *
 * The host recreates its `extraHeaders` object on every render, so it cannot be
 * an effect dependency without refetching in a loop. This gives the effects a
 * stable reference that still changes when the org or workspace does.
 * @param extraHeaders The headers the host passed in.
 */
export const useRequestHeaders = (
  extraHeaders?: PreviewRequestHeaders
): Record<string, string> | undefined => {
  const orgHeader = extraHeaders?.["org-id"] ?? "";
  const workspaceHeader = extraHeaders?.["workspace-id"] ?? "";

  return useMemo(
    () =>
      orgHeader || workspaceHeader
        ? { "org-id": orgHeader, "workspace-id": workspaceHeader }
        : undefined,
    [orgHeader, workspaceHeader]
  );
};

/**
 * Merges the list row the preview was opened from with the detail response.
 *
 * `/trials` and `/trial/{id}` return different field subsets — only the list row
 * carries `crop`, for example. Merging keeps what the row already had instead of
 * blanking it once the detail response lands, with the fresher detail values
 * winning wherever they are actually populated.
 * @param trialData The `/trial/{id}` response.
 * @param fallbackTrial The row the preview was opened from.
 */
export const useMergedTrial = (trialData: any, fallbackTrial: any): any =>
  useMemo(() => {
    if (!trialData) return fallbackTrial ?? null;
    if (!fallbackTrial) return trialData;

    const merged: Record<string, any> = { ...fallbackTrial };
    Object.entries(trialData as Record<string, any>).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        merged[key] = value;
      }
    });

    return merged;
  }, [trialData, fallbackTrial]);

export interface TrialDetailState {
  trialData: any;
  isLoading: boolean;
  hasError: boolean;
}

/**
 * Loads `/trial/{id}` whenever the sidebar is open for a trial.
 * @param trialId The trial to load.
 * @param open Whether the sidebar is open.
 * @param headers Stable request headers.
 */
export const useTrialDetail = (
  trialId: string,
  open: boolean,
  headers?: Record<string, string>
): TrialDetailState => {
  const [trialData, setTrialData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!trialId || !open) return;

    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    void (async () => {
      try {
        const response = await AxiosInstance.get(`/trial/${trialId}`, {
          headers,
        });
        if (!cancelled) setTrialData(response?.data);
      } catch (error) {
        console.error("Error fetching trial:", error);
        if (!cancelled) setHasError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [trialId, open, headers]);

  return { trialData, isLoading, hasError };
};

/**
 * Loads the linked protocol, which drives the Rates & Dosage section and the
 * protocol view. A failure here costs only those, never the whole preview.
 * @param open Whether the sidebar is open.
 * @param protocolId The linked protocol's id.
 * @param headers Stable request headers.
 */
export const useProtocolDetail = (
  open: boolean,
  protocolId: string,
  headers?: Record<string, string>
): any => {
  const [protocolData, setProtocolData] = useState<any>(null);

  useEffect(() => {
    if (!open || !protocolId) return;

    let cancelled = false;
    void (async () => {
      try {
        const response = await AxiosInstance.get(`/protocol/${protocolId}`, {
          headers,
        });
        if (!cancelled) setProtocolData(response?.data);
      } catch (error) {
        console.error("Error fetching protocol for trial preview:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, protocolId, headers]);

  return protocolData;
};

/**
 * Recovers the linked protocol's id from its name.
 *
 * Some trials arrive carrying the protocol's name but no id, which leaves the
 * View action with nothing to open and Rates & Dosage with no protocol to read.
 * The protocols list supports a `search` param, so the name is enough.
 * @param open Whether the sidebar is open.
 * @param name The linked protocol's name.
 * @param needsLookup False once an id is known from the payload.
 * @param orgId The organization to search within.
 * @param headers Stable request headers.
 */
export const useProtocolIdByName = (
  open: boolean,
  name: string,
  needsLookup: boolean,
  orgId?: string,
  headers?: Record<string, string>
): string => {
  const [protocolId, setProtocolId] = useState("");

  useEffect(() => {
    if (!open || !name || !needsLookup || !orgId) return;

    let cancelled = false;
    void (async () => {
      try {
        const params = new URLSearchParams({
          org_id: orgId,
          page: "1",
          limit: "20",
          archived: "false",
          search: name,
        });

        const response = await AxiosInstance.get(
          `/protocols?${params.toString()}`,
          { headers }
        );

        const rows = pickProtocolRows(response?.data);

        // Prefer an exact name match; a single search hit is good enough when
        // the backend has already narrowed it down.
        const match =
          rows.find((row) => row?.name === name) ??
          (rows.length === 1 ? rows[0] : undefined);

        if (!cancelled && match?.id) setProtocolId(String(match.id));
      } catch (error) {
        // The preview still works without it; only the protocol link is lost.
        console.error("Error resolving the linked protocol by name:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, name, needsLookup, orgId, headers]);

  return protocolId;
};

export interface YieldReadinessParams {
  open: boolean;
  /** The trial's fields: readiness is keyed on the field, not the trial. */
  fieldIds?: string[];
  trialId?: string;
  endDate?: string;
}

/**
 * Resolves the yield-analysis readiness behind the Data Readiness tag and the
 * Explorer link. Failures resolve to "not found" so the preview keeps rendering.
 * @param params The trial's property, fields, id and end date.
 */
export const useYieldReadiness = ({
  open,
  fieldIds,
  trialId,
  endDate,
}: YieldReadinessParams): YieldAnalysisReadiness => {
  const [readiness, setReadiness] = useState<YieldAnalysisReadiness>(NOT_READY);

  /*
   * The request is identified by its contents, not by prop identity: `fieldIds`
   * is a fresh array every render, and the list row and `/trial/{id}` can format
   * `end_date` differently for the same season — keying on the derived year
   * stops that difference re-firing the call once the detail lands.
   */
  const fieldKey = (fieldIds ?? []).join(",");
  const year = getYieldAnalysisYear(endDate);
  const requestKey = `${fieldKey}|${String(year ?? "")}|${trialId ?? ""}`;

  // The last request actually issued for this open sidebar. Readiness is one
  // question per open, so an unchanged key is never asked twice — including by
  // StrictMode's double-invoked effects in development.
  const issuedKey = useRef("");

  useEffect(() => {
    if (!open) {
      // Reopening asks again: a PENDING harvest may have finished since.
      issuedKey.current = "";
      return;
    }

    if (!fieldKey || issuedKey.current === requestKey) return;
    issuedKey.current = requestKey;

    let cancelled = false;
    setReadiness(NOT_READY);

    void (async () => {
      try {
        const result = await fetchYieldAnalysisReadiness({
          fieldIds: fieldKey.split(","),
          trialId,
          year,
        });
        if (!cancelled) setReadiness(result);
      } catch (error) {
        // Readiness gates one CTA, never a reason to break the preview.
        console.error("Error resolving yield analysis readiness:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, fieldKey, requestKey, trialId, year]);

  return readiness;
};
